import { useState } from 'react'
import {
  FileText,
  Download,
  Calendar,
  Filter,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Building,
  Activity,
  BarChart3,
  PieChart,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Mail,
  FileSpreadsheet,
  FileJson,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Save,
  Share2,
  Eye,
  EyeOff,
  ChevronRight,
  Plus,
  Trash2,
  Edit,
  Copy,
  Info
} from 'lucide-react'
import { Select } from '../components/ui/Select'

// Local export functions (replacing @ngc-medical/shared/utils)
const exportToCSV = async (data: any[], filename: string) => {
  try {
    const headers = Object.keys(data[0] || {})
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`
    link.click()
    URL.revokeObjectURL(url)
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

const exportToExcel = async (data: any[], filename: string, _sheetName: string) => {
  // For now, export as CSV (real Excel would need xlsx library)
  return exportToCSV(data, filename)
}

const exportToPDF = async (data: any[], filename: string, _title: string) => {
  // For now, just show the data (real PDF would need jspdf library)
  console.log('PDF Export:', { data, filename })
  alert('Exportação PDF não implementada. Use CSV ou Excel.')
  return { success: false, error: 'PDF export not implemented' }
}

const exportToJSON = async (data: any[], filename: string) => {
  try {
    const jsonContent = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.json`
    link.click()
    URL.revokeObjectURL(url)
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts'

interface ReportTemplate {
  id: string
  name: string
  description: string
  category: 'financial' | 'operational' | 'compliance' | 'performance' | 'custom'
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'on-demand'
  lastGenerated?: string
  nextScheduled?: string
  format: ('pdf' | 'excel' | 'json')[]
  recipients?: string[]
  filters?: any
  status: 'active' | 'inactive' | 'draft'
}

interface ReportExecution {
  id: string
  reportName: string
  generatedAt: string
  generatedBy: string
  format: 'pdf' | 'excel' | 'json'
  size: string
  status: 'completed' | 'failed' | 'processing'
  downloadUrl?: string
  error?: string
}

interface ReportSchedule {
  id: string
  reportId: string
  reportName: string
  frequency: string
  nextRun: string
  recipients: string[]
  enabled: boolean
}

export function Reports() {
  const [activeTab, setActiveTab] = useState<'templates' | 'generate' | 'history' | 'scheduled' | 'analytics'>('templates')
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'excel' | 'json'>('pdf')
  const [showNewReportModal, setShowNewReportModal] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Mock data for templates
  const reportTemplates: ReportTemplate[] = [
    {
      id: 'r-001',
      name: 'Relatório Financeiro Mensal',
      description: 'Resumo completo de receitas, despesas e lucro líquido',
      category: 'financial',
      frequency: 'monthly',
      lastGenerated: '2024-01-01 00:00:00',
      nextScheduled: '2024-02-01 00:00:00',
      format: ['pdf', 'excel'],
      recipients: ['cfo@ngcmed.com', 'admin@ngcmed.com'],
      status: 'active'
    },
    {
      id: 'r-002',
      name: 'Análise de Crescimento de Tenants',
      description: 'Métricas de aquisição e churn de clientes',
      category: 'operational',
      frequency: 'weekly',
      lastGenerated: '2024-01-15 09:00:00',
      nextScheduled: '2024-01-22 09:00:00',
      format: ['pdf', 'json'],
      recipients: ['growth@ngcmed.com'],
      status: 'active'
    },
    {
      id: 'r-003',
      name: 'Relatório de Compliance LGPD',
      description: 'Auditoria de conformidade com proteção de dados',
      category: 'compliance',
      frequency: 'quarterly',
      lastGenerated: '2023-10-01 00:00:00',
      nextScheduled: '2024-01-01 00:00:00',
      format: ['pdf'],
      recipients: ['legal@ngcmed.com', 'compliance@ngcmed.com'],
      status: 'active'
    },
    {
      id: 'r-004',
      name: 'Performance do Sistema',
      description: 'Métricas de uptime, latência e uso de recursos',
      category: 'performance',
      frequency: 'daily',
      lastGenerated: '2024-01-19 23:00:00',
      nextScheduled: '2024-01-20 23:00:00',
      format: ['json', 'excel'],
      recipients: ['devops@ngcmed.com'],
      status: 'active'
    },
    {
      id: 'r-005',
      name: 'Relatório de Faturamento Detalhado',
      description: 'Detalhamento completo de cobranças e pagamentos',
      category: 'financial',
      frequency: 'on-demand',
      lastGenerated: '2024-01-18 14:30:00',
      format: ['excel'],
      status: 'inactive'
    }
  ]

  // Mock execution history
  const executionHistory: ReportExecution[] = [
    {
      id: 'e-001',
      reportName: 'Relatório Financeiro Mensal',
      generatedAt: '2024-01-01 00:00:00',
      generatedBy: 'system',
      format: 'pdf',
      size: '2.4MB',
      status: 'completed',
      downloadUrl: '/reports/financial-2024-01.pdf'
    },
    {
      id: 'e-002',
      reportName: 'Análise de Crescimento de Tenants',
      generatedAt: '2024-01-15 09:00:00',
      generatedBy: 'system',
      format: 'json',
      size: '156KB',
      status: 'completed',
      downloadUrl: '/reports/growth-2024-w3.json'
    },
    {
      id: 'e-003',
      reportName: 'Performance do Sistema',
      generatedAt: '2024-01-19 23:00:00',
      generatedBy: 'system',
      format: 'excel',
      size: '890KB',
      status: 'completed',
      downloadUrl: '/reports/performance-2024-01-19.xlsx'
    },
    {
      id: 'e-004',
      reportName: 'Relatório de Compliance LGPD',
      generatedAt: '2024-01-20 10:30:00',
      generatedBy: 'admin@ngcmed.com',
      format: 'pdf',
      size: '5.2MB',
      status: 'failed',
      error: 'Timeout ao processar grande volume de dados'
    }
  ]

  // Mock scheduled reports
  const scheduledReports: ReportSchedule[] = [
    {
      id: 's-001',
      reportId: 'r-001',
      reportName: 'Relatório Financeiro Mensal',
      frequency: 'Todo dia 1 às 00:00',
      nextRun: '2024-02-01 00:00:00',
      recipients: ['cfo@ngcmed.com', 'admin@ngcmed.com'],
      enabled: true
    },
    {
      id: 's-002',
      reportId: 'r-002',
      reportName: 'Análise de Crescimento',
      frequency: 'Toda segunda às 09:00',
      nextRun: '2024-01-22 09:00:00',
      recipients: ['growth@ngcmed.com'],
      enabled: true
    },
    {
      id: 's-003',
      reportId: 'r-004',
      reportName: 'Performance do Sistema',
      frequency: 'Diário às 23:00',
      nextRun: '2024-01-20 23:00:00',
      recipients: ['devops@ngcmed.com'],
      enabled: true
    }
  ]

  // Chart data
  const revenueData = [
    { month: 'Jan', revenue: 125000, expenses: 45000, profit: 80000 },
    { month: 'Fev', revenue: 135000, expenses: 48000, profit: 87000 },
    { month: 'Mar', revenue: 142000, expenses: 52000, profit: 90000 },
    { month: 'Abr', revenue: 155000, expenses: 55000, profit: 100000 },
    { month: 'Mai', revenue: 168000, expenses: 58000, profit: 110000 },
    { month: 'Jun', revenue: 175000, expenses: 60000, profit: 115000 }
  ]

  const tenantGrowthData = [
    { month: 'Jan', newTenants: 45, churnedTenants: 8, totalTenants: 1250 },
    { month: 'Fev', newTenants: 52, churnedTenants: 5, totalTenants: 1297 },
    { month: 'Mar', newTenants: 68, churnedTenants: 12, totalTenants: 1353 },
    { month: 'Abr', newTenants: 75, churnedTenants: 10, totalTenants: 1418 },
    { month: 'Mai', newTenants: 82, churnedTenants: 15, totalTenants: 1485 },
    { month: 'Jun', newTenants: 90, churnedTenants: 18, totalTenants: 1557 }
  ]

  const planDistribution = [
    { name: 'Basic', value: 450, color: '#9CA3AF' },
    { name: 'Professional', value: 680, color: '#3B82F6' },
    { name: 'Enterprise', value: 427, color: '#8B5CF6' }
  ]

  const generateReport = async () => {
    if (!selectedTemplate) return

    setIsGenerating(true)

    try {
      // Generate mock data based on the template
      let mockData: any[] = []

      if (selectedTemplate.category === 'financial') {
        mockData = revenueData.map(item => ({
          Mês: item.month,
          Receita: `R$ ${item.revenue.toLocaleString()}`,
          Despesas: `R$ ${item.expenses.toLocaleString()}`,
          Lucro: `R$ ${item.profit.toLocaleString()}`
        }))
      } else if (selectedTemplate.category === 'operational') {
        mockData = tenantGrowthData.map(item => ({
          Mês: item.month,
          'Novos Tenants': item.newTenants,
          'Tenants Perdidos': item.churnedTenants,
          'Total Tenants': item.totalTenants
        }))
      } else {
        // Default generic data
        mockData = [
          { Item: 'Exemplo 1', Valor: '100', Status: 'Ativo' },
          { Item: 'Exemplo 2', Valor: '250', Status: 'Pendente' },
          { Item: 'Exemplo 3', Valor: '150', Status: 'Completo' }
        ]
      }

      // Export based on selected format
      let result
      switch (selectedFormat) {
        case 'csv':
          result = await exportToCSV(mockData, selectedTemplate.name)
          break
        case 'excel':
          result = await exportToExcel(mockData, selectedTemplate.name, selectedTemplate.name)
          break
        case 'pdf':
          result = await exportToPDF(mockData, selectedTemplate.name, selectedTemplate.name)
          break
        case 'json':
          result = await exportToJSON(mockData, selectedTemplate.name)
          break
        default:
          result = await exportToCSV(mockData, selectedTemplate.name)
      }

      if (result.success) {
        alert(`Relatório "${selectedTemplate.name}" gerado e baixado com sucesso!`)
      } else {
        alert(`Erro ao gerar relatório: ${result.error}`)
      }
    } catch (error) {
      alert('Erro ao processar relatório')
    } finally {
      setIsGenerating(false)
    }
  }

  const getCategoryColor = (category: string) => {
    switch(category) {
      case 'financial': return 'text-green-600 bg-green-100'
      case 'operational': return 'text-blue-600 bg-blue-100'
      case 'compliance': return 'text-orange-600 bg-orange-100'
      case 'performance': return 'text-purple-600 bg-purple-100'
      case 'custom': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getFormatIcon = (format: string) => {
    switch(format) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-600" />
      case 'excel': return <FileSpreadsheet className="w-4 h-4" />
      case 'json': return <FileJson className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Relatórios Avançados</h1>
            <p className="text-gray-600 mt-2">Gere e gerencie relatórios personalizados do sistema</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNewReportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              <Plus className="w-5 h-5" />
              Novo Relatório
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{reportTemplates.length}</p>
          <p className="text-xs text-gray-600 mt-1">Relatórios configurados</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <span className="text-xs text-gray-500">Agendados</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{scheduledReports.length}</p>
          <p className="text-xs text-gray-600 mt-1">Execuções automáticas</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-xs text-gray-500">Sucesso</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">94%</p>
          <p className="text-xs text-gray-600 mt-1">Taxa de sucesso</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <Download className="w-5 h-5 text-orange-600" />
            <span className="text-xs text-gray-500">Este mês</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">247</p>
          <p className="text-xs text-gray-600 mt-1">Relatórios gerados</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('templates')}
              className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-colors ${
                activeTab === 'templates'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-5 h-5" />
              Templates
            </button>
            <button
              onClick={() => setActiveTab('generate')}
              className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-colors ${
                activeTab === 'generate'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Play className="w-5 h-5" />
              Gerar Relatório
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-5 h-5" />
              Histórico
            </button>
            <button
              onClick={() => setActiveTab('scheduled')}
              className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-colors ${
                activeTab === 'scheduled'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-5 h-5" />
              Agendados
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              Analytics
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'templates' && (
            <div className="space-y-4">
              {reportTemplates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(template.category)}`}>
                          {template.category}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          template.status === 'active' ? 'bg-green-100 text-green-800' :
                          template.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {template.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Copy className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {template.frequency}
                      </div>
                      {template.lastGenerated && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Último: {template.lastGenerated}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        Formatos:
                        {template.format.map((fmt, i) => (
                          <span key={i} className="ml-1">
                            {getFormatIcon(fmt)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-all"
                    >
                      Gerar Agora
                    </button>
                  </div>

                  {template.recipients && template.recipients.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Mail className="w-4 h-4" />
                        <span>Destinatários:</span>
                        {template.recipients.map((email, i) => (
                          <span key={i} className="px-2 py-1 bg-white rounded border border-gray-200 text-xs">
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'generate' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecionar Template
                  </label>
                  <Select
                    onChange={(e) => setSelectedTemplate(
                      reportTemplates.find(t => t.id === e.target.value) || null
                    )}
                    options={[
                      { value: '', label: 'Escolha um template...' },
                      ...reportTemplates.map(template => ({
                        value: template.id,
                        label: template.name
                      }))
                    ]}
                    fullWidth
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Período
                  </label>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    options={[
                      { value: 'today', label: 'Hoje' },
                      { value: 'week', label: 'Esta Semana' },
                      { value: 'month', label: 'Últimos 30 dias' },
                      { value: 'quarter', label: 'Último Trimestre' },
                      { value: 'year', label: 'Último Ano' },
                      { value: 'custom', label: 'Personalizado' }
                    ]}
                    fullWidth
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Formato de Saída
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="pdf"
                        checked={selectedFormat === 'pdf'}
                        onChange={(e) => setSelectedFormat(e.target.value as any)}
                      />
                      <FileText className="w-4 h-4 text-red-600" />
                      PDF
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="excel"
                        checked={selectedFormat === 'excel'}
                        onChange={(e) => setSelectedFormat(e.target.value as any)}
                      />
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      Excel
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="json"
                        checked={selectedFormat === 'json'}
                        onChange={(e) => setSelectedFormat(e.target.value as any)}
                      />
                      <FileJson className="w-4 h-4 text-blue-600" />
                      JSON
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enviar por Email
                  </label>
                  <input
                    type="email"
                    placeholder="email@exemplo.com"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {selectedTemplate && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2">{selectedTemplate.name}</h3>
                  <p className="text-sm text-purple-700">{selectedTemplate.description}</p>

                  <div className="mt-4 flex items-center gap-3">
                    <button
                      onClick={generateReport}
                      disabled={isGenerating}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          Gerar Relatório
                        </>
                      )}
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-all">
                      <Eye className="w-5 h-5" />
                      Pré-visualizar
                    </button>
                  </div>
                </div>
              )}

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Dica</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Você pode agendar a geração automática de relatórios na aba "Agendados".
                      Configure frequência, destinatários e filtros personalizados.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Relatório</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Gerado em</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Por</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Formato</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Tamanho</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionHistory.map((execution) => (
                      <tr key={execution.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                          {execution.reportName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {execution.generatedAt}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {execution.generatedBy}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {getFormatIcon(execution.format)}
                            <span className="text-sm text-gray-600 uppercase">{execution.format}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {execution.size}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            execution.status === 'completed' ? 'bg-green-100 text-green-800' :
                            execution.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {execution.status === 'completed' ? 'Concluído' :
                             execution.status === 'processing' ? 'Processando' : 'Falhou'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {execution.status === 'completed' && (
                              <button className="p-1 text-blue-600 hover:text-blue-700">
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <Share2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'scheduled' && (
            <div className="space-y-4">
              {scheduledReports.map((schedule) => (
                <div key={schedule.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{schedule.reportName}</h3>
                      <p className="text-sm text-gray-600 mt-1">{schedule.frequency}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {schedule.enabled ? 'Ativo' : 'Pausado'}
                      </span>
                      <button
                        className={`p-1 ${
                          schedule.enabled ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'
                        }`}
                      >
                        {schedule.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Próxima Execução</p>
                      <p className="font-medium text-gray-900">{schedule.nextRun}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Destinatários</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {schedule.recipients.map((email, i) => (
                          <span key={i} className="text-xs text-gray-700">
                            {email}{i < schedule.recipients.length - 1 && ','}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-4">Receita vs Despesas</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Receita" />
                        <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Despesas" />
                        <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={2} name="Lucro" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-4">Crescimento de Tenants</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={tenantGrowthData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip />
                        <Area type="monotone" dataKey="totalTenants" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Total" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-4">Distribuição por Plano</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={planDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {planDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-4">Relatórios Gerados por Mês</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { month: 'Jan', reports: 180 },
                        { month: 'Fev', reports: 195 },
                        { month: 'Mar', reports: 210 },
                        { month: 'Abr', reports: 225 },
                        { month: 'Mai', reports: 238 },
                        { month: 'Jun', reports: 247 }
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip />
                        <Bar dataKey="reports" fill="#8b5cf6" name="Relatórios" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
